import { useTranslation } from 'react-i18next'
import { Globe, Scale, Gauge, FileText, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import type { AssessmentStatus } from '@/types'

const STEPS = [
  { key: 'search', icon: Globe },
  { key: 'analysis', icon: Scale },
  { key: 'scoring', icon: Gauge },
  { key: 'report', icon: FileText },
] as const

function activeIndex(status: AssessmentStatus): number {
  switch (status) {
    case 'SEARCHING': return 0
    case 'ANALYZING': return 1
    case 'COMPLETED': return STEPS.length
    default: return 0
  }
}

/**
 * Animated stepper shown while the analysis pipeline runs:
 * web search → regulatory analysis → scoring → PDF report.
 */
export function PipelineTimeline({ status }: { status: AssessmentStatus }) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const current = activeIndex(status)
  // Docente/segreteria (richiedenti) non ricevono il report PDF: vedono solo
  // l'esito a video, quindi l'ultimo passo non deve menzionare il report.
  const isRequester = user?.role === 'DOCENTE' || user?.role === 'SEGRETERIA'

  return (
    <div className="rounded-xl border border-mim-blue/20 bg-gradient-to-br from-mim-lightblue/60 to-card dark:from-primary/10 dark:to-card p-6">
      <p className="text-sm font-semibold text-mim-blue dark:text-primary mb-6">
        <span className="ellipsis-anim">{t('assessment.pipeline.title')}</span>
      </p>

      <ol className="flex items-start">
        {STEPS.map(({ key, icon: Icon }, i) => {
          const done = i < current
          const active = i === current
          return (
            <li key={key} className={cn('flex items-start', i < STEPS.length - 1 && 'flex-1')}>
              <div className="flex flex-col items-center w-16 md:w-20">
                <div
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors duration-500',
                    done && 'bg-verde border-verde text-white',
                    active && 'bg-mim-blue border-mim-blue text-white animate-pulse-ring',
                    !done && !active && 'bg-card border-border text-muted-foreground/50'
                  )}
                >
                  {done ? (
                    <Check className="w-5 h-5 animate-step-pop" />
                  ) : (
                    <Icon className={cn('w-5 h-5', active && 'animate-pulse')} />
                  )}
                </div>
                <span
                  className={cn(
                    'mt-2 text-[11px] md:text-xs leading-tight text-center font-medium transition-colors',
                    done && 'text-verde-dark dark:text-verde',
                    active && 'text-mim-blue dark:text-primary font-semibold',
                    !done && !active && 'text-muted-foreground/60'
                  )}
                >
                  {key === 'report' && isRequester
                    ? t('assessment.pipeline.reportRequester' as any)
                    : t(`assessment.pipeline.${key}` as any)}
                </span>
              </div>

              {i < STEPS.length - 1 && (
                <div className="flex-1 h-0.5 mt-5 mx-1 rounded-full overflow-hidden bg-border">
                  <div
                    className={cn(
                      'h-full w-full transition-opacity duration-500',
                      i < current ? 'bg-verde' : i === current ? 'flow-line animate-flow' : 'opacity-0'
                    )}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ol>

      <p className="text-xs text-muted-foreground mt-5">
        {t('assessment.pipeline.hint')}
      </p>
    </div>
  )
}
